import React, { Fragment, useCallback, useState } from 'react';
import { array, bool, string, number, object } from 'prop-types';
import { Link, navigate } from '@reach/router';
import { linkTo } from 'utils/links';
import { Trans, Plural, t } from '@lingui/macro';
import { I18n } from '@lingui/react';
import { useDispatch } from 'redux-react-hook';
import classNames from 'classnames';
import format from 'date-fns/format'

// Redux
import { play } from 'store/modules/player/actions';
import { postAddToPlaylist } from 'store/modules/users/actions';
import { likeAlbum, likeSong } from 'store/modules/music/actions';

// Components
import Button from 'components/Button';
import Dropdown from 'components/Dropdown';
import Image from 'components/Image';
import Play from 'components/Icon/Play';
import PlaylistForm from 'containers/Playlists/PlaylistForm';
import Toast from 'components/Toast';
import Heart from 'components/Icon/Heart';
import HeartFill from 'components/Icon/HeartFill';
import FolderPlus from 'components/Icon/FolderPlus';
import Globe from 'components/Icon/Globe';

export default function Detail(props) {
  const { model, list, image, title, link1, link1Label, link2, link2Label, released, total, playlists, isSong, isLoggedIn } = props;
  const [showForm, toggleForm] = useState(false);
  const [showToast, toggleToast] = useState(false);
  const dispatch = useDispatch();
  const onPlayClick = useCallback(() => {
    const first = model.sound ? model : list[0];
    dispatch(play(first, list));
  }, [model, list]);
  const onLikeClick = useCallback(() => {
    if (isLoggedIn) {
      if (isSong) {
        dispatch(likeSong(model));
      } else {
        dispatch(likeAlbum(model));
      }
    } else {
      navigate(linkTo('/auth/sign-in'));
    }
  }, [model]);
  const onPlaylistClick = useCallback((name, playlist) => {
    if (isLoggedIn) {
      if (playlist.id > 0) {
        dispatch(postAddToPlaylist(playlist, list))
          .then(() => {
            toggleToast(true);
          });
      } else {
        toggleForm(true);
      }
    } else {
      navigate(linkTo('/auth/sign-in'));
    }
  }, [model, playlists]);
  const onShareClick = useCallback(() => {
    console.log(model);
  }, [model]);
  const onSave = useCallback((playlist) => {
    toggleForm(false);
    dispatch(postAddToPlaylist(playlist, list))
      .then(() => {
        toggleToast(true);
      });
  }, [model]);
  const onCancel = useCallback(() => {
    toggleForm(false);
  }, [model]);
  const onCloseToast = useCallback(() => {
    toggleToast(false);
  }, [model]);
  const LikeIcon = model.isFavorite ? HeartFill : Heart;

  return (
    <Fragment>
      <Image src={image} className="w-full rounded bg-cover bg-list-active" background />
      <h2 className="leading-tight font-sans text-default mt-8 mb-2 text-2xl xl:text-3xl">{title}</h2>
      <p
        className={classNames('leading-tight text-base text-secondary leading-normal', {
          'mb-8': !link2,
        })}>
        { link1 &&
          <Link
            to={link1}
            className="text-secondary hover:underline"
          >
            {link1Label}
          </Link>
        } 
        {released && <Fragment> &bull; {format(new Date(released), 'YYYY')}</Fragment>}
        {total > 0 && <Fragment> &bull; <Plural value={total} one="# Song" other="# Songs"/></Fragment>}
      </p>
      { link2 &&
        <p className="mb-8 leading-normal">
          <Link
            to={link2}
            className="text-secondary hover:underline"
          >
            {link2Label}
          </Link>
        </p>
      }
      <div className="flex items-center">
        <Button onClick={onPlayClick} className="mr-2 lg:mr-4">
          <Play width={20} height={20} className="inline fill-current align-top mr-1" /> <Trans>Play</Trans>
        </Button>
        <Button onClick={onLikeClick} type="plain" className="mx-2">
          <LikeIcon
            width={24}
            height={24}
            className={classNames('inline fill-current hover:text-default', {
              'text-primary': model.isFavorite,
              'text-secondary': !model.isFavorite,
            })} 
          />
        </Button>
        <I18n>
          {({ i18n }) =>
            <PlaylistButton i18n={i18n} playlists={playlists} onPlaylistClick={onPlaylistClick} />
          }
        </I18n>
        {/* <Button onClick={onShareClick} type="plain" className="mx-2">
          <Globe width={24} height={24} className="inline fill-current text-secondary hover:text-default" />
        </Button> */}
      </div>
      {showForm && <PlaylistForm onSave={onSave} onCancel={onCancel} />}
      {showToast &&
        <Toast type="success" onClose={onCloseToast}>
          <Plural value={list.length} one="# song added to playlist" other="# songs added to playlist" />
        </Toast>
      }
    </Fragment>
  );
}

Detail.propTypes = {
  model: object,
  list: array,
  image: string,
  title: string,
  link1: string,
  link1Label: string,
  link2: string,
  link2Label: string,
  released: string,
  total: number,
  playlists: array,
  isSong: bool,
};

Detail.defaultProps = {
  model: {},
  isSong: true,
  isFavorite: false,
};

function PlaylistButton({ i18n, playlists, onPlaylistClick }) {
  const options = [{ id: 0, title: i18n._(t`Create Playlist`) }, ...playlists]

  return (
    <Dropdown
      options={options}
      valueField="id"
      labelField="title"
      onChangeSelection={onPlaylistClick}
      caret={false}
      anchor="bottom-left"
      className="mx-2"
    >
      <FolderPlus width={24} height={24} className="inline fill-current text-secondary hover:text-default" />
    </Dropdown>
  );
}
